import {View, PixelRatio, Platform, StyleSheet} from 'react-native';
import Styles from 'src/components/Styles';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import PersonRedesign from 'src/components/img/svg/PersonRedesign';
import RewardRedesign from 'src/components/img/svg/RewardRedesign';
import Localized from 'src/constants/AppStrings';
import AccountStore from 'src/stores/AccountStore';
import AVText from 'src/components/elements/AVText';
import SnackDetailsRedesign from 'src/components/img/svg/SnackDetailsRedesign';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

export function getBalanceRollOverIcon(): JSX.Element {
  return <RewardRedesign size={28} />;
}

export function getFundButtonForBalanceScreen(fundBtnCallback): JSX.Element {
  return (
    <RoundedAppButton
      buttonType={RoundedAppButtonType.Outline}
      buttonContainerStyle={getBalanceItemStyleDescriptor().button}
      buttonViewStyle={{borderWidth: 2}}
      textStyle={getBalanceItemStyleDescriptor().fundButtonText}
      accessibilityLabelValue={Localized.Labels.fund_account}
      aria-label={Localized.Labels.fund_account}
      onPress={fundBtnCallback}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
      titleText={Localized.Labels.fund}
      testID="qa_btn_Fund"
    />
  );
}

export function getDetailsButtonForBalanceScreen(
  detailsBtnCallback,
): JSX.Element {
  return (
    <RoundedAppButton
      buttonType={RoundedAppButtonType.Outline}
      buttonContainerStyle={getBalanceItemStyleDescriptor().button}
      buttonViewStyle={{borderWidth: 2}}
      textStyle={getBalanceItemStyleDescriptor().fundButtonText}
      accessibilityLabelValue={Localized.Labels.details}
      aria-label={Localized.Labels.details}
      onPress={detailsBtnCallback}
      maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
      titleText={Localized.Labels.details}
      testID="qa_btn_Details"
    />
  );
}

export function balanceTypeStyleDescriptor(): {[k: string]: number | string} {
  return {
    fontFamily: Styles.FontFamily.gothamRegular,
    fontSize: getLayoutDescriber().subHeaderFontSize(),
    color: Styles.tabBarTextColor,
  };
}

export function SaleDetailRowStyleDescriptor(): {[k: string]: number | string} {
  return {
    alignItems: 'center',
    paddingLeft: Styles.Spacing.m1,
    justifyContent: 'space-between',
    paddingVertical: Styles.Spacing.m0,
    paddingRight: 0,
    flexDirection: 'row',
  };
}

export function SaleDetailSupLblMaxFontSizeMultipleDescriptor(): number {
  return Styles.FontSizeMultiplier.maxfm10;
}

export function getBalanceStripeIcon(): JSX.Element {
  return <PersonRedesign size={24} />;
}

export function SaleDetailNameStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    fontFamily: Styles.FontFamily.gothamRegular,
    flex: 1,
    fontSize: getLayoutDescriber().bodyFontSize(),
    marginRight: Styles.Spacing.m2,
    color: Styles.darkColor,
    flexWrap: 'wrap',
    fontWeight: '400',
  };
}

export function getDashedStyleDescriptor(): {
  [k: string]: {[kk: string]: number};
} {
  return {
    dashStyle: {
      dashThickness: PixelRatio.roundToNearestPixel(1),
      dashLength: PixelRatio.roundToNearestPixel(5),
    },
  };
}

export function SaleDetailAmtStyleDescriptor(): {[k: string]: number | string} {
  return {
    fontSize: getLayoutDescriber().bodyFontSize(),
    color: Styles.darkColor,
    fontFamily: Styles.FontFamily.gothamRegular,
    right: 3,
    fontWeight: '400',
  };
}

export function SaleDetailModMaxFontSizeMultipleDescriptor(): number {
  return Styles.FontSizeMultiplier.maxfm8;
}

export function SaleDetailModNameStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    flexDirection: 'row',
    marginRight: Styles.Spacing.m2,
    maxWidth: '70%',
    flex: 1,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
    color: Styles.Colors.PayNew.darkHuesBase04,
  };
}

export function PurchaseDetailFormateedDateStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    color: Styles.darkColor,
    fontWeight: '400',
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
  };
}

export function payLocationAddr(isHistory) {
  return (
    isHistory && {
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontFamily: Styles.FontFamily.gothamRegular,
    }
  );
}

export function payOrderStatus(isHistory) {
  return isHistory && {marginBottom: 20};
}

export function payOrderNumber(isHistory) {
  return isHistory && {marginTop: 20};
}

export function getDashColor(): {[k: string]: {[kk: string]: string}} {
  // Keep Aramark dash color
  return {
    dashStyle: {dashColor: '#999'},
  };
}

export function getSaleAmountTextUI(modPrice: number, saleCurrency: string) {
  // Redesign-style (no leading “+”), Gotham + Aramark colors
  return (
    <AVFormattedCurrency
      currency={saleCurrency}
      style={getStyleDescriptor().modifierAmount}
      value={modPrice}
      aria-label={`$${modPrice}`}
      accessibilityLabel={`$${modPrice}`}
      accessible
      testID="qa_Receipt.ModifierPrice_txt"
      nativeID="qa_Receipt.ModifierPrice_txt"
    />
  );
}

export function getStyleDescriptor(
  isHistory?: boolean,
  transTypeDsply?: string,
): {[k: string]: {[kk: string]: number | string}} {
  return {
    remainingBalance: {
      color: Styles.Colors.PayNew.darkHuesBase04,
      fontFamily: Styles.FontFamily.gothamRegular,
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontWeight: '400',
      height: 22,
    },
    buttonSpacing: {flex: 1, justifyContent: 'center', alignItems: 'center'},
    instructions: {
      fontSize: Styles.Fonts.f8,
      marginTop: Styles.Spacing.m2,
      color: Styles.darkColor,
      fontStyle: 'italic',
    },
    location: {
      marginTop: Styles.Spacing.m3,
      color: Styles.tabBarTextColor,
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontWeight: 'bold',
      fontFamily: Styles.FontFamily.gothamRegular,
    },
    pickupTime: {
      color: Styles.darkColor,
      fontSize: getLayoutDescriber().bodyFontSize(),
      marginTop: Styles.Spacing.m1 + 4,
      fontFamily: Styles.FontFamily.gothamRegular,
    },
    orderStatus: {
      fontWeight: '700',
      fontSize: getLayoutDescriber().bodyFontSize(),
      color: Styles.darkColor,
      fontFamily: Styles.FontFamily.gothamRegular,
    },
    orderStatusValue: {
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontWeight: isHistory ? '400' : '700',
      fontFamily: Styles.FontFamily.gothamRegular,
      color: Styles.darkColor,
    },
    shareIconColor: {color: Styles.Colors.Aramark.primary},
    sectionMarginLeftWithZero: {marginLeft: -4},
    charityRow: {backgroundColor: 'transparent'},
    points: {
      marginTop: Styles.Spacing.m1,
      color: Styles.Colors.Aramark.primary,
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontWeight: '700',
      fontFamily: Styles.FontFamily.gothamRegular,
      fontStyle: 'italic',
    },
    sectionMarginLeft: {marginLeft: -8, alignItems: 'flex-start'},
    pickupDate: {
      fontSize: getLayoutDescriber().bodyFontSize(),
      marginTop: Styles.Spacing.m2,
      fontFamily: Styles.FontFamily.gothamRegular,
      color: Styles.tabBarTextColor,
      fontWeight: isHistory ? '400' : 'normal',
    },
    summaryExtra: {marginTop: 4, marginBottom: Styles.Spacing.m1},
    summaryTopMargin: {marginTop: 0},
    sectionMarginRightWithZero: {marginRight: -5},
    modifierAmount: {
      marginRight: 2,
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontFamily: Styles.FontFamily.gothamRegular,
      color: Styles.Colors.PayNew.darkHuesBase04,
    },
    dashStyleBorder: {
      borderRadius: 100,
      height: PixelRatio.roundToNearestPixel(2),
      marginBottom: Styles.Spacing.m2,
      marginTop: Styles.Spacing.m2 + Styles.Spacing.m1,
      overflow: 'hidden',
      width: '100%',
    },
    totalLabel: {
      color: Styles.tabBarTextColor,
      fontSize: getLayoutDescriber().subHeaderFontSize(),
      fontWeight: '700',
      marginBottom: Styles.Spacing.m1,
      marginTop: -1 * Styles.Spacing.m1,
      fontFamily: Styles.FontFamily.gothamRegular,
    },
    rowReverse: {flexDirection: 'row-reverse', flexWrap: 'wrap'},
  };
}

export function balanceItemRoundedBtnColorDescriptor(): string {
  return Styles.Colors.Aramark.primary;
}

export function PurchaseDetailIdStyleDescriptor(isHistory?: boolean): {
  [k: string]: number | string;
} {
  return {
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m1,
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontSize: getLayoutDescriber().bodyFontSize(),
    marginRight: Styles.Spacing.m3,
    fontWeight: isHistory ? '400' : 'normal',
    fontFamily: Styles.FontFamily.gothamRegular,
  };
}

export function getActionBtns(
  handleDone: () => void,
  handleEmailReceipt: () => void,
) {
  return (
    <View
      style={[
        Styles.Style.row,
        {
          bottom: Styles.Spacing.m3,
          position: 'absolute',
          right: Styles.Spacing.m3,
        },
      ]}
    >
      <RoundedAppButton
        buttonType={RoundedAppButtonType.Outline}
        titleText={Localized.Buttons.email_receipt}
        onPress={handleEmailReceipt}
        buttonViewStyle={{backgroundColor: 'white'}}
        textStyle={{
          fontWeight: Platform.OS === 'android' ? 'bold' : '700',
          fontFamily: Styles.FontFamily.gothamRegular,
          fontSize: getLayoutDescriber().bodyFontSize(),
        }}
        testID="qa_Receipt.Share_Btn"
      />
      <RoundedAppButton
        buttonType={RoundedAppButtonType.Solid}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
        accessibilityLabelValue={Localized.Buttons.done}
        aria-label={Localized.Buttons.done}
        onPress={handleDone}
        buttonContainerStyle={{marginLeft: Styles.Spacing.m3}}
        textStyle={Styles.Style.actionButtonText}
        titleText={Localized.Buttons.done}
        testID="qa_Receipt.Done_Btn"
      />
    </View>
  );
}

export function fundingdetailTrans(): {[k: string]: number | string} {
  return {
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
    fontWeight: '400',
    marginBottom: 10,
  };
}

export function fundingdetailAccountFndgTxt(): {[k: string]: number | string} {
  return {
    color: Styles.tabBarTextColor,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
    fontWeight: '400',
  };
}

export function PurchaseDetailDateFormatStyleDescriptor(): string {
  return 'MMMM D, YYYY [at] h:mm A';
}

export function PurchaseDetailSubtotalStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    fontSize: getLayoutDescriber().bodyFontSize(),
    marginRight: Styles.Spacing.m3,
    color: Styles.Colors.PayNew.black01,
    fontFamily: Styles.FontFamily.gothamRegular,
  };
}

export function PurchaseDetailDepositStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    fontFamily: Styles.FontFamily.gothamRegular,
    color: Styles.Colors.PayNew.black01,
    marginBottom: Styles.Spacing.m1,
    fontSize: getLayoutDescriber().bodyFontSize(),
  };
}

export function balanceAmountStyleDescriptor(): {[k: string]: number | string} {
  return {
    fontFamily: Styles.FontFamily.gothamRegular,
    fontWeight: '700',
    fontSize: getLayoutDescriber().subHeaderFontSize(),
  };
}

export function PurchaseDetailtaxStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    marginRight: Styles.Spacing.m3,
    color: Styles.Colors.PayNew.black01,
    fontFamily: Styles.FontFamily.gothamRegular,
    fontSize: getLayoutDescriber().bodyFontSize(),
  };
}

export function getBalanceItemStyleDescriptor(): {
  [k: string]: {[kk: string]: number | string};
} {
  return {
    container: {
      justifyContent: 'flex-start',
      paddingHorizontal: Styles.Spacing.m3,
      flex: 1,
      borderBottomColor: Styles.lightGray,
      width: '100%',
      alignItems: 'center',
      paddingVertical: Styles.Spacing.m3,
      flexDirection: 'row',
    },
    button: {
      right: Styles.Spacing.m3,
      top: Styles.Spacing.m3 + 2,
      alignSelf: 'center',
      position: 'absolute',
    },
    fundButtonText: {
      paddingHorizontal: Styles.Spacing.m2,
      fontFamily: Styles.FontFamily.gothamRegular,
      fontSize: getLayoutDescriber().bodyFontSize(),
      fontWeight: '700',
    },
    balanceContainer: {
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      paddingHorizontal: Styles.Spacing.m3,
    },
    totalText: {
      color: Styles.tabBarTextColor,
      fontSize: getLayoutDescriber().subHeaderFontSize(),
      fontWeight: '700',
    },
    promoContainer: {backgroundColor: Styles.tabBarBackgroundColor, flex: 1},
  };
}

export function PurchaseDetailDiscountStyleDescriptor(): {
  [k: string]: number | string;
} {
  return {
    fontFamily: Styles.FontFamily.gothamRegular,
    color: Styles.Colors.Aramark.primary,
    fontSize: getLayoutDescriber().bodyFontSize(),
  };
}

export function fundingDetailAccount(styles, props) {
  return (
    <View style={styles.rightView}>
      <AVFormattedCurrency
        style={[fundingdetailAccountFndgTxt()]}
        value={props.detail.Amount}
        testID={'qa_accountFundingValue_txt'}
        accessibilityLabel={`$${props.detail.Amount}`}
        aria-label={`$${props.detail.Amount}`}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
        currency={AccountStore.getCurrency()}
      />
    </View>
  );
}

export function PurchaseHistorySubtotalStyleDescriptor(isHistory?: boolean): {
  [k: string]: number | string;
} {
  return {
    color: Styles.Colors.PayNew.black01,
    marginBottom: Styles.Spacing.m1,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
    fontWeight: isHistory ? '400' : 'normal',
  };
}

export function fundingHeaderTitle() {
  return Localized.Labels.funding;
}

export function promoCreditTotalText() {
  return Localized.Labels.total;
}

export function fundingDetailDate(): {[k: string]: number | string} {
  return {
    color: Styles.tabBarTextColor,
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontFamily: Styles.FontFamily.gothamRegular,
    fontWeight: '400',
  };
}

export function fundingDetailTotal(): {[k: string]: number | string} {
  return {
    color: Styles.tabBarTextColor,
    fontSize: getLayoutDescriber().subHeaderFontSize(),
    fontWeight: '700',
    fontFamily: Styles.FontFamily.gothamRegular,
  };
}

export function rightViewStyle() {
  return {flex: 0.3, justifyContent: 'flex-end', alignItems: 'flex-end'};
}

export function leftViewStyle() {
  return {justifyContent: 'flex-start', alignItems: 'flex-start'};
}

export function promoCreditAmount(styles, props) {
  return (
    <View style={styles.rightView}>
      <AVFormattedCurrency
        style={[fundingdetailAccountFndgTxt()]}
        value={props.detail.Amount}
        accessibilityLabel={`$${props.detail.Amount}`}
        aria-label={`$${props.detail.Amount}`}
        maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
        currency={AccountStore.getCurrency()}
      />
    </View>
  );
}

export function historyEmailReceiptBtn(
  handleEmailReceipt,
  styles,
  detailID?: string,
  _isUSACheck?: string,
  isHistory?: boolean,
) {
  return (
    isHistory && (
      <RoundedAppButton
        buttonType={RoundedAppButtonType.Solid}
        titleText={Localized.Buttons.email_receipts}
        onPress={() => handleEmailReceipt(detailID || null)}
        buttonContainerStyle={{
          right: Styles.Spacing.m3,
          bottom: Styles.Spacing.m3,
          position: 'absolute',
        }}
        buttonViewStyle={{
          paddingHorizontal: Styles.Spacing.m4,
          paddingVertical: Styles.Spacing.m1 + Styles.Spacing.m2,
          minHeight: 42,
        }}
        textStyle={{
          fontWeight: Platform.OS === 'android' ? 'bold' : '700',
          fontFamily: Styles.FontFamily.gothamRegular,
          fontSize: getLayoutDescriber().bodyFontSize(),
          color: Styles.white,
          textAlign: 'center',
        }}
        testID="qa_Receipt.Share_Btn"
      />
    )
  );
}

export function fundingDetailContainer() {
  return {flex: 1, flexDirection: 'row'};
}
export function redemptionDetailContainer() {
  return {flex: 1, flexDirection: 'row'};
}

export function payDashedLine() {
  // Keep Aramark dash color
  return '#999';
}
export function fundingContainerColor() {
  return Styles.Colors.Aramark.neutralHuesBase13;
}

export function payPrefixTxt() {
  return 'Your ';
}

export function paySnackContainer() {
  return {top: -20};
}

export function paySnackRow() {
  return {paddingLeft: 10};
}

export function giftIconSnack() {
  return <SnackDetailsRedesign fill={Styles.Colors.Aramark.primary} />;
}

export function payBalanceType(styles) {
  return styles.paybalanceTypeDisplay;
}
export function payDteDisplay(styles) {
  return styles.payDteDisplay;
}

export function paystatus(styles) {
  return {...styles.paydescription, marginTop: -10};
}
export function paydescription(styles) {
  return styles.paydescription;
}

export function getEUDigitalRecipts(vatLabel: string, euTaxPercent: string) {
  return (
    <AVText
      accessibilityLabel={`${vatLabel} ${euTaxPercent}`}
      maxFontSizeMultiplier={SaleDetailModMaxFontSizeMultipleDescriptor()}
      aria-label={`${vatLabel} ${euTaxPercent}`}
      style={styles.euTaxStyle}
    >
      {`${vatLabel} ${euTaxPercent}`}
    </AVText>
  );
}

const styles = StyleSheet.create({
  euTaxStyle: {
    fontSize: Styles.Fonts.f8,
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontFamily: Styles.FontFamily.gothamRegular,
    paddingLeft: Styles.Spacing.m1,
  },
});

export default {
  payPrefixTxt,
  payDashedLine,
  getStyleDescriptor,
  fundingdetailTrans,
  fundingDetailContainer,
  redemptionDetailContainer,
  paySnackContainer,
  getActionBtns,
  PurchaseDetailDepositStyleDescriptor,
  getBalanceRollOverIcon,
  balanceAmountStyleDescriptor,
  leftViewStyle,
  paySnackRow,
  payDteDisplay,
  balanceTypeStyleDescriptor,
  getBalanceItemStyleDescriptor,
  paystatus,
  SaleDetailModMaxFontSizeMultipleDescriptor,
  SaleDetailRowStyleDescriptor,
  SaleDetailSupLblMaxFontSizeMultipleDescriptor,
  SaleDetailNameStyleDescriptor,
  getSaleAmountTextUI,
  getFundButtonForBalanceScreen,
  getDetailsButtonForBalanceScreen,
  balanceItemRoundedBtnColorDescriptor,
  payLocationAddr,
  SaleDetailAmtStyleDescriptor,
  SaleDetailModNameStyleDescriptor,
  PurchaseDetailFormateedDateStyleDescriptor,
  PurchaseDetailDateFormatStyleDescriptor,
  PurchaseDetailIdStyleDescriptor,
  getBalanceStripeIcon,
  fundingdetailAccountFndgTxt,
  PurchaseDetailSubtotalStyleDescriptor,
  payBalanceType,
  PurchaseDetailtaxStyleDescriptor,
  PurchaseDetailDiscountStyleDescriptor,
  fundingDetailDate,
  getDashedStyleDescriptor,
  rightViewStyle,
  fundingDetailAccount,
  getDashColor,
  fundingContainerColor,
  paydescription,
  giftIconSnack,
  PurchaseHistorySubtotalStyleDescriptor,
  fundingHeaderTitle,
  payOrderNumber,
  fundingDetailTotal,
  payOrderStatus,
  historyEmailReceiptBtn,
  getEUDigitalRecipts,
  promoCreditAmount,
  promoCreditTotalText,
};
